import { useCallback, useEffect } from 'react';

import { useTracking } from '@change-corgi/core/react/tracking';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useQueryParamValue } from 'src/app/shared/hooks/location';

import {
	FORM_SUBMIT_ERROR_EVENT,
	FORM_SUBMIT_EVENT,
	FORM_SUBMIT_SUCCESS_EVENT,
	PREVIEW_OPEN_EVENT,
	SUGGESTED_TEMPLATE_NOTICE,
	UPDATE_FORM_VEW_EVENT,
} from '../../../../constants';
import { useCreatePetitionUpdateContext } from '../../context';
import type { UseTrackSubmitEventsProps } from '../../types';

export function useTrackSubmitEvents({
	error,
	success,
	image,
	submittedValues,
	removePetitionUpdateFormValues,
	removePetitionUpdateMedia,
}: UseTrackSubmitEventsProps) {
	const track = useTracking();
	const sourceLocation = useQueryParamValue('source_location');
	const isTemplateFilled = useCreatePetitionUpdateContext();
	const {
		data: {
			petition: {
				id,
				displayTitle,
				signatureState: {
					signatureCount: { displayed },
				},
				petitionStarterUpdates: { totalCount },
			},
		},
	} = useStarterDashboardV2PageData();

	useEffect(() => {
		void track(UPDATE_FORM_VEW_EVENT, {
			petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
			source_location: sourceLocation,
			victory: false,
			edit: false,
		});
	}, [track, isTemplateFilled, sourceLocation]);

	useEffect(() => {
		if (success?.updateId) {
			void track(FORM_SUBMIT_SUCCESS_EVENT, {
				update_id: success.updateId,
				description: submittedValues.description || '',
				user_petition_info_state: {
					petition_id: id,
					display_title: displayTitle,
					own_petition: true,
					can_edit: true,
					organization_id: '', // TODO put real organization ID
					num_displayed_signatures: displayed,
					is_first_update: totalCount === 0,
				},
				has_image: !!image,
				embedded_media: submittedValues.embeddedMediaUrl,
			});
			removePetitionUpdateFormValues();
			removePetitionUpdateMedia();
		}
	}, [
		submittedValues,
		success,
		track,
		id,
		displayTitle,
		displayed,
		totalCount,
		removePetitionUpdateFormValues,
		removePetitionUpdateMedia,
		image,
	]);

	useEffect(() => {
		if (error?.message) {
			void track(FORM_SUBMIT_ERROR_EVENT, {
				petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
				source_location: sourceLocation,
				type: error.type,
				message: error.message,
				edit: false,
			});
		}
	}, [error, track, isTemplateFilled, sourceLocation]);

	const trackOnPreview = useCallback(() => {
		void track(PREVIEW_OPEN_EVENT, {
			petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
			source_location: sourceLocation,
			victory: false,
			edit: false,
		});
	}, [track, isTemplateFilled, sourceLocation]);

	const trackSubmit = useCallback(
		(hasImage = false) => {
			void track(FORM_SUBMIT_EVENT, {
				petition_update_template: isTemplateFilled ? SUGGESTED_TEMPLATE_NOTICE : undefined,
				source_location: sourceLocation,
				has_image: hasImage,
				edit: false,
			});
		},
		[track, isTemplateFilled, sourceLocation],
	);

	return {
		trackOnPreview,
		trackSubmit,
	};
}
